import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import {
  catchError,
  concatMap,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  OperatorFunction,
  Subscription,
  take,
  tap,
} from 'rxjs';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';

import { ButtonColorsEnum } from '@tablesready/button-group-control';
import { User } from '@shared/user/models';
import { InviteRole, InviteStatus } from '@shared/invite/enums';
import { Invite, InviteRequest } from '@shared/invite/models';
import { CreateLocationUserRequest } from '@shared/location/models';
import { UserLocationRole } from '@shared/user/enums';
import {
  ValidationService,
  InfoModalComponent,
  NAME_PATTERN,
  emailValidator,
} from '@tablesready/common-ui';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { InviteApiService, LocationApiService } from '../../../../../services';
import { UserWithLocation } from '../../models';
import { EditUserComponent } from '../edit-user/edit-user.component';

@Component({
  selector: 'tablesready-location-invite-user',
  templateUrl: './invite-user.component.html',
})
export class InviteUserComponent implements OnInit, OnDestroy {
  @Input() data: Observable<[string, User[], User[], User[], UserWithLocation[], Invite[]]>;
  private locationId: string;
  private usersToInvite: User[] = [];
  private admins: User[] = [];
  private accountUsers: User[] = [];
  private locationUsers: UserWithLocation[] = [];
  private invites: Invite[] = [];

  icon = AppIcon;
  color = AppIconColor;
  inProgress = false;
  readonly = false;
  role: string;
  name: string;
  email: string;
  buttonsColor = ButtonColorsEnum;

  form: UntypedFormGroup;
  userRoles = [
    {name: 'USER', value: InviteRole.user},
    {name: 'MANAGER', value: InviteRole.manager},
  ];

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly alertsService: AlertsService,
    private readonly validationService: ValidationService,
    private readonly inviteApiService: InviteApiService,
    private readonly locationApiService: LocationApiService,
    private readonly modalController: FrontendModalService,
  ) {}

  ngOnInit(): void {
    const dataSubscription = this.data
      .pipe(tap(([locationId, usersToInvite, accountUsers, admins, locationUsers, invites]) => {
        this.locationId = locationId;
        this.usersToInvite = usersToInvite;
        this.accountUsers = accountUsers;
        this.admins = admins;
        this.locationUsers = locationUsers;
        this.invites = invites;
      }))
      .subscribe();
    this.subscriptions.add(dataSubscription);

    this.form = this.fb.group({
      email: [
        '',
        {
          validators: [
            Validators.required,
            Validators.email,
            Validators.maxLength(100),
          ],
          asyncValidators: emailValidator(this.validationService),
          updateOn: 'blur',
        },
      ],
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(NAME_PATTERN),
        ],
      ],
      role: [InviteRole.user],
    });

    const formSub = this.form.valueChanges
      .pipe(tap((value) => {
        this.checkIfUserExistsByEmail(value.email);
      }))
      .subscribe();
    this.subscriptions.add(formSub);
  }

  private checkIfUserExistsByEmail(email: string): void {
    const alreadyExistsUser = this.locationUsers.find(item => item.email === email);
    if (!alreadyExistsUser) {
      return;
    }

    this.modalController.create<InfoModalComponent, boolean>(InfoModalComponent, {
      message: `${alreadyExistsUser.name || 'This user'} already has access to this location. Click below to modify their settings.`,
      displayButton: true,
      buttonText: `View Profile for ${alreadyExistsUser.name || 'user'}`,
    })
      .pipe(
        take(1),
        filter(data => !!data),
        concatMap(() => this.modalController.dismissAsync()),
        delay(1),
        tap(() => {
          this.modalController.create(EditUserComponent, {user: alreadyExistsUser})
            .pipe(take(1))
            .subscribe();
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  send(): void {
    this.validationService.validateForm(this.form);
    if (!this.form.valid || this.inProgress) {
      return;
    }

    this.inProgress = true;
    const value: FormData = this.form.value;

    if (this.assignUserIfExists(value)) {
      return;
    }

    if (this.checkAdmin(value)) {
      return;
    }

    if (this.checkInvites(value)) {
      return;
    }

    const userAlreadyExistsInAccount = this.accountUsers.find(user => user.email === value.email);
    if (userAlreadyExistsInAccount) {
      this.alertsService.addInfo('The user has been already added to the account.');

      return;
    }

    this.sendInvite(value);
  }

  userSelected(
    email?: NgbTypeaheadSelectItemEvent<string>,
    name?: NgbTypeaheadSelectItemEvent<string>,
  ): void {
    const user = this.usersToInvite.find((item) => {
      if (email) {
        return item.email === email.item;
      }

      return item.name === name.item;
    });

    this.emailControl.setValue(user.email);
    this.emailControl.updateValueAndValidity();
    this.nameControl.setValue(user.name);
    this.nameControl.updateValueAndValidity();
    this.readonly = !!user?._id;
  }

  searchByEmail: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.usersToInvite.filter(user => (user.email || '')
        .toLowerCase().includes(term.toLowerCase()))
        .map(item => item.email),
      ),
    );

  searchByName: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.usersToInvite.filter(user => user.name
        .toLowerCase().includes(term.toLowerCase()))
        .map(item => item.name),
      ),
    );

  reset(): void {
    this.emailControl.reset();
    this.nameControl.reset();
  }

  private assignUserIfExists(value: FormData): boolean {
    const user = this.usersToInvite.find(item => item.email === value.email);
    if (user) {
      const data: CreateLocationUserRequest = {
        userId: user._id,
        role: value.role === InviteRole.manager ? UserLocationRole.manager : UserLocationRole.user,
        locationId: this.locationId,
      };

      this.locationApiService.createLocationUser(data)
        .pipe(
          tap(() => {
            this.alertsService.addSuccess('User successfully added to location.');
            this.close();

            this.inProgress = false;
          }),
          catchError(err => {
            this.alertsService.addError('An error occurred during adding user to location.');
            this.inProgress = false;

            return of(err);
          }),
        )
        .subscribe();

      return true;
    }

    return false;
  }

  private checkAdmin(value: FormData): boolean {
    const admin = this.admins.find(admin => admin.email === value.email);
    if (admin) {
      this.alertsService.addInfo(`This user has an account administrator role. Unable to invite him.`);
      this.inProgress = false;

      return true;
    }

    return false;
  }

  private checkInvites(value: FormData): boolean {
    const invite = this.invites.find(invite => invite.email === value.email && invite.status !== InviteStatus.declined)
    if (invite) {
      if (invite.role === InviteRole.admin) {
        this.alertsService.addInfo(`The user has been already invited to the account administrator role. Please contact administrator of your account.`);
      } else {
        this.alertsService.addInfo('This user has already been invited to this location. Please cancel the existing invite if you want to change their role or contact your account admin for help.');
      }
      this.inProgress = false;

      return true;
    }

    return false;
  }

  private sendInvite(value: FormData): void {
    const data: InviteRequest = {
      email: value.email,
      role: value.role,
      name: value.name,
      locations: [this.locationId],
    };

    this.inviteApiService.send(data)
      .pipe(
        tap(() => {
          this.alertsService.addSuccess('Invite successfully sent.', 'Success:');
          this.close();
          this.inProgress = false;
        }),
        catchError(error => {
          this.inProgress = false;

          if (error instanceof HttpErrorResponse && error.status === 409) {
            this.alertsService.addError(error.error.message);
          } else {
            this.alertsService.addError('An error occurred during sending invite.');
          }

          return of(error);
        }),
      )
      .subscribe();
  }

  get roleControl(): AbstractControl {
    return this.form.get('role');
  }

  get nameControl(): AbstractControl {
    return this.form.get('name');
  }

  get emailControl(): AbstractControl {
    return this.form.get('email');
  }

  close(): void {
    this.modalController.dismiss();
  }
}

interface FormData {
  email: string;
  name: string;
  role: InviteRole;
}
